import React, { Component } from 'react';
import { Box, Text } from 'grommet';
import { Button, Select, Input, Checkbox, message, Modal } from 'antd';
import { OPS_DATA_URL } from '../config';
import { authenticateToken, getAuthEmailFromToken } from '../utils/auth';

const { Option } = Select;
const PHASES = ['PREPARATION', 'MAINTENANCE WINDOW', 'POST UPGRADE ACTIVITY'];

export default class MaintenanceExecutionChecklist extends Component {
  constructor(props) {
    super(props);
    this.state = {
      labels: [],
      farmsByLabel: {},
      selectedLabel: undefined,
      selectedFarm: undefined,
      instances: [],
      selectedInstanceId: undefined,
      selectedInstance: null,
      loading: false,
      farmFilter: '' // New state for farm filter
    };
  }

  componentDidMount() {
    this.loadLabels();
    this.loadInstances();
  }

  loadLabels = async () => {
    try {
      const res = await fetch(`${OPS_DATA_URL}/maintenances`);
      if (!res.ok) return;
      const data = await res.json();
      const farmsByLabel = {};
      (Array.isArray(data) ? data : []).forEach((item) => {
        const label = (item.label && item.label.trim()) ? item.label.trim() : 'UNLABELED';
        const farm = (item.farm && String(item.farm).trim()) ? String(item.farm).trim() : '';
        if (!farmsByLabel[label]) farmsByLabel[label] = [];
        if (farm && !farmsByLabel[label].includes(farm)) {
          farmsByLabel[label].push(farm);
        }
      });
      const labels = Object.keys(farmsByLabel).sort((a, b) => a.localeCompare(b));
      const selectedLabel = labels[0];
      const selectedFarm = selectedLabel ? farmsByLabel[selectedLabel][0] : undefined;
      this.setState({ labels, farmsByLabel, selectedLabel, selectedFarm });
    } catch (e) {
      // ignore list fetch errors in initial render
    }
  }

  loadInstances = async () => {
    try {
      const res = await fetch(`${OPS_DATA_URL}/maintenance-checklists/instances`);
      if (!res.ok) {
        message.error('Failed to load execution checklists.');
        return;
      }
      const instances = await res.json();
      const first = instances[0];
      this.setState({
        instances,
        selectedInstanceId: first ? first.id : undefined,
        selectedInstance: first || null
      });
    } catch (e) {
      message.error('Network error while loading execution checklists.');
    }
  }

  createFromLabel = async () => {
    const token = authenticateToken();
    const operator = getAuthEmailFromToken() || '';
    const { selectedLabel, selectedFarm } = this.state;
    if (!selectedLabel) {
      message.error('Please select a label.');
      return;
    }
    if (!selectedFarm) {
      message.error('Please select a farm.');
      return;
    }
    if (!token) {
      message.error('Please sign in again.');
      return;
    }
    this.setState({ loading: true });
    try {
      const res = await fetch(`${OPS_DATA_URL}/maintenance-checklists/instances/from-label`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ label: selectedLabel, farm: selectedFarm, operator })
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        const available = Array.isArray(err.available_labels) && err.available_labels.length
          ? ` Available labels: ${err.available_labels.join(', ')}`
          : '';
        const farms = Array.isArray(err.available_farms) && err.available_farms.length
          ? ` Available farms for label: ${err.available_farms.join(', ')}`
          : '';
        message.error(`${err.error || 'Failed to create checklist.'}${available}${farms}`);
        this.setState({ loading: false });
        return;
      }
      message.success('Checklist created from label.');
      this.setState({ loading: false });
      this.loadInstances();
    } catch (e) {
      this.setState({ loading: false });
      message.error('Network error while creating checklist.');
    }
  }

  selectInstance = (id) => {
    const selected = this.state.instances.find((item) => item.id === id) || null;
    this.setState({ selectedInstanceId: id, selectedInstance: selected ? { ...selected } : null });
  }

  updateTask = (index, key, value) => {
    this.setState((prev) => {
      if (!prev.selectedInstance) return null;
      const tasks = Array.isArray(prev.selectedInstance.tasks) ? [...prev.selectedInstance.tasks] : [];
      tasks[index] = {
        ...tasks[index],
        [key]: value,
        updated_at: Date.now()
      };
      return {
        selectedInstance: {
          ...prev.selectedInstance,
          tasks
        }
      };
    });
  }

  addTask = (phase) => {
    this.setState((prev) => {
      if (!prev.selectedInstance) return null;
      const tasks = Array.isArray(prev.selectedInstance.tasks) ? [...prev.selectedInstance.tasks] : [];
      tasks.push({
        id: `task-${Date.now()}-${tasks.length + 1}`,
        title: '',
        phase,
        is_default: false,
        is_done: false,
        order: tasks.length + 1,
        notes: '',
        owner: '',
        created_at: Date.now(),
        updated_at: Date.now()
      });
      return {
        selectedInstance: {
          ...prev.selectedInstance,
          tasks
        }
      };
    });
  }

  removeTask = (index) => {
    this.setState((prev) => {
      if (!prev.selectedInstance) return null;
      const tasks = (prev.selectedInstance.tasks || []).filter((_, i) => i !== index).map((task, i) => ({
        ...task,
        order: i + 1
      }));
      return {
        selectedInstance: {
          ...prev.selectedInstance,
          tasks
        }
      };
    });
  }

  deleteChecklist = () => {
    const { selectedInstance } = this.state;
    if (!selectedInstance) return;
    Modal.confirm({
      title: 'Delete Checklist',
      content: `Are you sure you want to delete the checklist "${selectedInstance.label || 'UNLABELED'} | ${((selectedInstance.farm || ((selectedInstance.related_farms || [])[0])) || 'N/A').toUpperCase()}"? This cannot be undone.`,
      okText: 'Delete',
      okType: 'danger',
      cancelText: 'Cancel',
      onOk: async () => {
        const token = authenticateToken();
        if (!token) {
          message.error('Please sign in again.');
          return;
        }
        try {
          const res = await fetch(`${OPS_DATA_URL}/maintenance-checklists/instances/${selectedInstance.id}`, {
            method: 'DELETE',
            headers: { 'Authorization': `Bearer ${token}` }
          });
          if (!res.ok) {
            const err = await res.json().catch(() => ({}));
            message.error(err.error || 'Failed to delete checklist.');
            return;
          }
          message.success('Checklist deleted.');
          this.setState({ selectedInstanceId: undefined, selectedInstance: null });
          this.loadInstances();
        } catch (e) {
          message.error('Network error while deleting checklist.');
        }
      }
    });
  }

  saveChecklist = async () => {
    const token = authenticateToken();
    const operator = getAuthEmailFromToken() || '';
    const { selectedInstance } = this.state;
    if (!selectedInstance || !token) {
      message.error('Please sign in again.');
      return;
    }
    const tasks = (selectedInstance.tasks || [])
      .map((task, i) => ({ ...task, order: i + 1, title: (task.title || '').trim() }))
      .filter((task) => task.title);

    try {
      const res = await fetch(`${OPS_DATA_URL}/maintenance-checklists/instances`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          id: selectedInstance.id,
          status: selectedInstance.status || 'OPEN',
          tasks,
          operator
        })
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        message.error(err.error || 'Failed to save checklist.');
        return;
      }
      message.success('Checklist saved.');
      this.loadInstances();
    } catch (e) {
      message.error('Network error while saving checklist.');
    }
  }

  renderPhaseTasks = (phase) => {
    const instance = this.state.selectedInstance;
    const tasks = instance && Array.isArray(instance.tasks)
      ? instance.tasks.filter((task) => task.phase === phase)
      : [];
    if (!instance) return null;
    return (
      <Box key={phase} margin={{ bottom: 'medium' }}>
        <Box direction='row' justify='between' align='center' margin={{ bottom: 'xsmall' }}>
          <Text weight='bold'>{phase}</Text>
          <Button onClick={() => this.addTask(phase)}>Add Task</Button>
        </Box>
        {tasks.length === 0 ? (
          <Text size='small'>No tasks in this phase.</Text>
        ) : tasks.map((task) => {
          const absoluteIndex = (instance.tasks || []).findIndex((item) => item.id === task.id);
          return (
            <Box key={task.id} direction='row' gap='small' margin={{ bottom: 'xsmall' }} align='center'>
              <Checkbox
                checked={!!task.is_done}
                onChange={(event) => this.updateTask(absoluteIndex, 'is_done', event.target.checked)}
              />
              <Input
                style={{ minWidth: '320px' }}
                value={task.title}
                onChange={(event) => this.updateTask(absoluteIndex, 'title', event.target.value)}
                placeholder='Task title'
              />
              {/* Owner field hidden as per user request */}
              {/* <Input
                style={{ minWidth: '180px' }}
                value={task.owner || ''}
                onChange={(event) => this.updateTask(absoluteIndex, 'owner', event.target.value)}
                placeholder='Owner'
              /> */}
              <Input
                style={{ minWidth: '240px' }}
                value={task.notes || ''}
                onChange={(event) => this.updateTask(absoluteIndex, 'notes', event.target.value)}
                placeholder='Notes'
              />
              <Button onClick={() => this.removeTask(absoluteIndex)}>Remove</Button>
            </Box>
          );
        })}
      </Box>
    );
  }

  render() {
    const { labels, farmsByLabel, selectedLabel, selectedFarm, instances, selectedInstanceId, selectedInstance, loading, farmFilter } = this.state;
    const tasks = selectedInstance && Array.isArray(selectedInstance.tasks) ? selectedInstance.tasks : [];
    const completed = tasks.filter((task) => task && task.is_done).length;
    const farmOptions = selectedLabel ? (farmsByLabel[selectedLabel] || []) : [];

    // Collect all unique farms from checklist instances for filter dropdown
    const allFarms = Array.from(new Set(instances.map(item => (item.farm || ((item.related_farms || [])[0]) || '').toUpperCase()).filter(Boolean)));

    // Filter checklist instances by selected farm filter (if set)
    const filteredInstances = farmFilter
      ? instances.filter(item => ((item.farm || ((item.related_farms || [])[0]) || '').toUpperCase() === farmFilter))
      : instances;

    return (
      <Box align='start' width='100%' gap='small'>
        <Text size='xlarge' weight='bold'>Maintenance Execution Checklist</Text>
        <Box direction='row' gap='small' align='center'>
          <Select
            style={{ minWidth: '240px' }}
            value={selectedLabel}
            onChange={(value) => this.setState({ selectedLabel: value, selectedFarm: (farmsByLabel[value] || [])[0] })}
          >
            {labels.map((label) => <Option key={label} value={label}>{label}</Option>)}
          </Select>
          <Select
            style={{ minWidth: '180px' }}
            value={selectedFarm}
            onChange={(value) => this.setState({ selectedFarm: value })}
          >
            {farmOptions.map((farm) => <Option key={farm} value={farm}>{farm.toUpperCase()}</Option>)}
          </Select>
          <Button type='primary' loading={loading} onClick={this.createFromLabel}>Create Checklist From Label</Button>
        </Box>

        {/* Farm filter dropdown */}
        <Box direction='row' gap='small' align='center' margin={{ top: 'small', bottom: 'small' }}>
          <Text>Filter by Farm:</Text>
          <Select
            style={{ minWidth: '180px' }}
            allowClear
            value={farmFilter || undefined}
            placeholder='All Farms'
            onChange={(value) => this.setState({ farmFilter: value || '' })}
          >
            {allFarms.map((farm) => <Option key={farm} value={farm}>{farm}</Option>)}
          </Select>
        </Box>

        <Box direction='row' gap='small' align='center'>
          <Text>Checklist:</Text>
          <Select
            style={{ minWidth: '520px' }}
            value={selectedInstanceId}
            onChange={this.selectInstance}
          >
            {filteredInstances.map((item) => (
              <Option key={item.id} value={item.id}>{`${item.label || 'UNLABELED'} | ${((item.farm || ((item.related_farms || [])[0])) || 'N/A').toUpperCase()} | ${item.status || 'OPEN'}`}</Option>
            ))}
          </Select>
          <Button type='primary' onClick={this.saveChecklist} disabled={!selectedInstance}>Save</Button>
          <Button type='danger' onClick={this.deleteChecklist} disabled={!selectedInstance}>Delete</Button>
        </Box>

        {selectedInstance ? (
          <Box width='100%' margin={{ top: 'small' }}>
            <Text weight='bold'>Progress: {completed}/{tasks.length}</Text>
            <Box direction='row' gap='medium' margin={{ top: 'xsmall', bottom: 'small' }}>
              <Text>Label: {selectedInstance.label}</Text>
              <Text>Farm: {((selectedInstance.farm || ((selectedInstance.related_farms || [])[0])) || 'N/A').toUpperCase()}</Text>
              <Text>Status: {selectedInstance.status}</Text>
              <Text>Farms: {(selectedInstance.related_farms || []).join(', ') || 'N/A'}</Text>
            </Box>
            {PHASES.map(this.renderPhaseTasks)}
          </Box>
        ) : (
          <Text>No checklist instances available yet.</Text>
        )}
      </Box>
    );
  }
}
